import {
  useCallback,
  useState,
  type ChangeEvent,
  type SubmitEvent,
} from "react";
import { useNavigate } from "react-router";
import { catchError, of, tap } from "rxjs";

import { APP_ROUTES } from "@/core/constants";
import { AppStore } from "@/core/data_sources/store/app";
import { Button, TextInput } from "@/core/ui/atoms";

import { AuthStore } from "../../../../core/data_sources/store/auth";

export function SignInPage() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const handleSubmit = useCallback(
    (ev: SubmitEvent<HTMLFormElement>) => {
      ev.preventDefault();
      setError(null);
      AppStore.instance.actions.isLoading(true);
      AuthStore.instance.actions
        .signIn(credentials)
        .pipe(
          tap(() => navigate(APP_ROUTES.superAdmin.dashboard.path)),
          catchError(() => {
            setError("Wrong credentials, please try again.");
            return of(null);
          }),
          tap(() => AppStore.instance.actions.isLoading(false)),
        )
        .subscribe();
    },
    [credentials, navigate],
  );
  const setEmailValue = useCallback(
    (ev: ChangeEvent<HTMLInputElement, HTMLInputElement>): void =>
      setCredentials((prev) => ({
        ...prev,
        email: ev.target.value,
      })),
    [],
  );
  const setPasswordValue = useCallback(
    (ev: ChangeEvent<HTMLInputElement, HTMLInputElement>): void =>
      setCredentials((prev) => ({
        ...prev,
        password: ev.target.value,
      })),
    [],
  );
  return (
    <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(135deg,#f5f8fa_0%,#e5edf2_100%)] p-5">
      <section className="w-full max-w-[380px] rounded-xl border border-border bg-surface px-9 py-10 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
        <div className="mb-1 flex items-center justify-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-[7px] bg-cta text-sm font-bold text-white">
            S
          </div>
          <div className="text-[15px] font-semibold text-ink">SiteHub</div>
        </div>
        <div className="mb-7 text-center text-[11px] font-semibold uppercase tracking-[0.12em] text-cta">
          Super Admin
        </div>
        <h1 className="mb-1.5 text-center text-lg font-semibold text-ink">
          Sign in to your account
        </h1>
        <p className="mb-6 text-center text-[13px] text-ink-muted">
          Internal access only · {window.location.host}
        </p>

        <form onSubmit={handleSubmit}>
          <label className="mb-3.5 block">
            <span className="mb-1.5 block text-xs font-medium text-ink-muted">
              Email
            </span>
            <TextInput
              autoFocus
              className="bg-surface px-3 py-2"
              onChange={setEmailValue}
              placeholder="your.email@company.com"
              type="email"
              value={credentials.email}
            />
          </label>

          <label className="mb-3.5 block">
            <span className="mb-1.5 block text-xs font-medium text-ink-muted">
              Password
            </span>
            <TextInput
              className="bg-surface px-3 py-2"
              onChange={setPasswordValue}
              placeholder="••••••••"
              type="password"
              value={credentials.password}
            />
            {!error ? null : (
              <span className="mt-1 block text-[11px] text-critical">
                {error}
              </span>
            )}
          </label>

          <Button
            className="w-full justify-center py-2.5"
            type="submit"
            variant="primary"
          >
            Sign in
          </Button>
        </form>

        <button
          className="mx-auto mt-3 block border-0 bg-transparent text-xs text-cta"
          type="button"
        >
          Forgot password?
        </button>

        <div className="mt-[18px] rounded-control border border-[#fcd34d] bg-[#fffbeb] p-2.5 text-[11px] text-[#78350f] hidden">
          <strong>Demo mode:</strong> any credentials work. 2FA flow is
          simulated.
        </div>
      </section>
    </div>
  );
}
